import { GarageListItemBadge } from "@App/Components/GarageListItem/GarageListItemBadge";
import { useAssetUrl } from "@App/hooks/useAssetUrl";
import { useConfig } from "@App/hooks/useConfig";
import { useCountry } from "@App/hooks/useCountry";
import type { State } from "@App/state";
import type { Garage, Service } from "@App/types";
import { GarageIsAuthorizedWorkshop, GarageIsBookable } from "@App/utils/conditionals/garages";
import {
  OrganizationIsMFS,
  OrganizationIsMobilityConcept,
} from "@App/utils/conditionals/organization";
import { ServiceIsVehicleReturn } from "@App/utils/conditionals/services";
import { Transition } from "@headlessui/react";
import React, { StrictMode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GarageCoverImage } from "./GarageCoverImage";
import { GarageDetails } from "./GarageDetails";
import { GarageNextAppointment } from "./GarageNextAppointment";
import { GaragePricingAndProperties } from "./GaragePricingAndProperties";

type GarageListItemProps = {
  garage: Garage;
  showAsNonBookable: boolean;
  prefix?: string;
  onClick: (garage: Garage) => void;
  nearest?: boolean | null;
  earliestAvailable?: boolean | null;
  bestReviewed?: boolean | null;
  authorized?: boolean | null;
  highlighted?: boolean | null;
  preferredPartner?: boolean | null;
  service?: Service;
};

export const GarageListItem = React.memo(
  ({
    garage,
    prefix,
    onClick,
    nearest,
    earliestAvailable,
    bestReviewed,
    authorized,
    highlighted,
    preferredPartner,
    service: serviceProp,
    showAsNonBookable,
  }: GarageListItemProps) => {
    const { t } = useTranslation();
    const { url } = useAssetUrl();
    const { displayPremiumServices, organization } = useConfig();
    const countryCode = useCountry();

    const reduxService = useSelector((state: State) => state.orders.service);

    const service = useMemo(() => serviceProp ?? reduxService, [serviceProp, reduxService]);

    const [showPhoneNumber, setShowPhoneNumber] = useState(false);

    const bookable = useMemo(() => GarageIsBookable(garage), [garage]);

    const handleClick = useCallback(() => {
      if (bookable && !showAsNonBookable) onClick(garage);
    }, [bookable, showAsNonBookable, onClick, garage]);

    const handleTogglePhone = useCallback(() => {
      setShowPhoneNumber(true);
    }, []);

    const isVehicleReturn = ServiceIsVehicleReturn(service);

    const hideEarliestSlot =
      (OrganizationIsMFS(organization) || OrganizationIsMobilityConcept(organization)) &&
      isVehicleReturn;

    const hasBadges = nearest || earliestAvailable || bestReviewed || authorized;

    const showPreferredStyle = Boolean(preferredPartner);

    return (
      <div className="w-full">
        <Transition
          appear
          show
          as="div"
          enter="transition transform duration-300 ease-out"
          enterFrom="translate-x-4 opacity-0"
          enterTo="translate-x-0 opacity-100"
          leave="transition transform duration-300 ease-in"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <StrictMode>
            <div
              className={`garageItem relative rounded w-full lg:max-w-full
                        ${highlighted && (bookable || showPreferredStyle) ? "border border-primary" : ""}
                        ${hasBadges ? "lg:pt-5 xl:pt-0" : ""}
                        ${
                          bookable
                            ? `lg:min-h-[300px] lg:pb-5 pb-1 ${
                                showPreferredStyle
                                  ? "bg-[color-mix(in_srgb,var(--color-primary)_5%,transparent)]"
                                  : "bg-white"
                              }`
                            : "bg-gray-200 lg:py-0 py-2 "
                        }

                        ${!bookable && !hasBadges && (GarageIsAuthorizedWorkshop(garage) || showPreferredStyle) ? "pt-6 lg:pt-6" : ""}
                        ${bookable && !hasBadges && GarageIsAuthorizedWorkshop(garage) ? "pt-3 lg:pt-0" : ""}
                        shadow text-sm mb-3 lg:px-5`}
            >
              {hasBadges && (
                <GarageListItemBadge
                  nearest={nearest}
                  earliestAvailable={earliestAvailable}
                  authorized={authorized}
                  bestReviewed={bestReviewed}
                  garage={garage}
                  makeRoomForRibbon={showPreferredStyle}
                />
              )}

              {/* hasBadges only happens in B2C, for B2B we only want to have the authorized workshop badge */}
              {!hasBadges && GarageIsAuthorizedWorkshop(garage) && (
                <GarageListItemBadge
                  authorized={true}
                  garage={garage}
                  makeRoomForRibbon={showPreferredStyle}
                />
              )}

              {showPreferredStyle && (
                <div className="absolute top-0 right-0 z-10 bg-primary text-white  font-bold px-4 py-2 rounded-bl">
                  {t("badges.recommended")}
                </div>
              )}

              <GarageCoverImage
                garage={garage}
                service={service}
                url={url}
                organization={organization}
                bookable={bookable}
              />

              <GarageDetails
                garage={garage}
                bookable={bookable}
                service={service}
                prefix={prefix}
                handleClick={handleClick}
                showPhoneNumber={showPhoneNumber}
                setShowPhoneNumber={setShowPhoneNumber}
                displayPremiumServices={displayPremiumServices}
                showAsNonBookable={showAsNonBookable}
                t={t}
              />

              <GaragePricingAndProperties
                garage={garage}
                bookable={bookable}
                service={service}
                showAsNonBookable={showAsNonBookable}
                organization={organization}
                displayPremiumServices={displayPremiumServices}
                showPhoneNumber={showPhoneNumber}
                handleTogglePhone={handleTogglePhone}
                handleClick={handleClick}
                t={t}
              />

              <GarageNextAppointment
                garage={garage}
                showAsNonBookable={showAsNonBookable}
                bookable={bookable}
                service={service}
                organization={organization}
                handleClick={handleClick}
                showPhoneNumber={showPhoneNumber}
                setShowPhoneNumber={setShowPhoneNumber}
                showEarliestSlot={!hideEarliestSlot}
                countryCode={countryCode}
                t={t}
              />
            </div>
          </StrictMode>
        </Transition>
      </div>
    );
  },
);
